import type {StateCreator} from 'zustand'

export type SuggestionIdentifier = {
  commentDatabaseId: number
  suggestionIndex: number
}

export interface SuggestionBatchSlice {
  suggestionBatch: {
    activeBatch: SuggestionIdentifier[]
    isSuggestionBatchPending: boolean
  }
  suggestionBatchActions: {
    addSuggestionToBatch: (suggestionId: SuggestionIdentifier) => void
    removeSuggestionFromBatch: (suggestionId: SuggestionIdentifier) => void
    clearSuggestionBatch: () => void
    loadActiveBatchFromStorage: (activeBatch: SuggestionIdentifier[]) => void
  }
}

export type SuggestionBatchSliceOpts = {
  initialActiveBatch?: SuggestionIdentifier[]
}

export const createSuggestionBatchSlice =
  (opts: SuggestionBatchSliceOpts): StateCreator<SuggestionBatchSlice, [], [], SuggestionBatchSlice> =>
  set => ({
    suggestionBatch: {
      activeBatch: opts.initialActiveBatch ?? [],
      isSuggestionBatchPending: (opts.initialActiveBatch?.length ?? 0) > 0,
    },
    suggestionBatchActions: {
      addSuggestionToBatch: (suggestionId: SuggestionIdentifier) =>
        set(state => {
          const exists = state.suggestionBatch.activeBatch.some(
            id =>
              id.commentDatabaseId === suggestionId.commentDatabaseId &&
              id.suggestionIndex === suggestionId.suggestionIndex,
          )
          if (exists) {
            return state
          }
          const newActiveBatch = [...state.suggestionBatch.activeBatch, suggestionId]

          return {
            suggestionBatch: {
              ...state.suggestionBatch,
              activeBatch: newActiveBatch,
              isSuggestionBatchPending: newActiveBatch.length > 0,
            },
          }
        }),
      removeSuggestionFromBatch: (suggestionId: SuggestionIdentifier) =>
        set(state => {
          const newActiveBatch = state.suggestionBatch.activeBatch.filter(
            id =>
              !(
                id.commentDatabaseId === suggestionId.commentDatabaseId &&
                id.suggestionIndex === suggestionId.suggestionIndex
              ),
          )

          return {
            suggestionBatch: {
              ...state.suggestionBatch,
              activeBatch: newActiveBatch,
              isSuggestionBatchPending: newActiveBatch.length > 0,
            },
          }
        }),
      clearSuggestionBatch: () =>
        set(state => {
          return {
            suggestionBatch: {
              ...state.suggestionBatch,
              activeBatch: [],
              isSuggestionBatchPending: false,
            },
          }
        }),
      loadActiveBatchFromStorage: (activeBatch: SuggestionIdentifier[]) =>
        set(state => ({
          suggestionBatch: {
            ...state.suggestionBatch,
            activeBatch,
            isSuggestionBatchPending: activeBatch.length > 0,
          },
        })),
    },
  })
